import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { useAppSelector } from '@/redux';
import { selectLastOrder, selectShippingList } from '@/redux/slices/checkout.slice';
import { selectCartContent } from '@/redux/slices/cart.slice';
import { selectStoreId } from '@/redux/slices/root.slice';
import { PRICE } from '@/constants/price';
import { STORE_ID } from '@/constants/stores';
import { computeAdditionalFees } from '@/utils/cartUtils';

export const useSidebarPricesSection = (isReportStep: boolean, shouldRemoveAdditionalFeesFromTotalShippingCost: boolean) => {
  function getPriceLine() {
    return {
      shouldShow: true,
      label: t('account.orders.details.subTotal'),
      value: isUsa ?
        finalContentToComputePricesOn.totalProductHT :
        (
          finalContentToComputePricesOn.totalProduct ||
          (finalContentToComputePricesOn.totalProductHT + finalContentToComputePricesOn.totalTVA)
        )
    };
  }

  function getTotalTva() {
    return {
      shouldShow: isUsa,
      label: t('account.orders.details.taxes'),
      value: finalContentToComputePricesOn.totalTVA
    };
  }

  function getShippingCosts() {
    if (finalContentToComputePricesOn.shippingPrice < 0 || Number.isNaN(finalContentToComputePricesOn.shippingPrice)) {
      return { shouldShow: false };
    }

    if (shouldRemoveAdditionalFeesFromTotalShippingCost) {
      const shippingValue = computeShippingCostValueWithShippingFromFullList();
      const value = shippingValue !== PRICE.FREE ?
        finalContentToComputePricesOn.shippingPrice - computeAdditionalFees(finalContentToComputePricesOn, t).value :
        PRICE.FREE;
      return {
        shouldShow: true,
        label: t('account.orders.details.shippingCost'),
        value
      };
    }
    return {
      shouldShow: true,
      label: t('account.orders.details.shippingCost'),
      value: finalContentToComputePricesOn.shippingPrice
    };
  }

  function computeShippingCostValueWithShippingFromFullList() {
    const getShippingModeSelected = (shippingId?: string) => {
      for (const [_, shippingList] of shippingModesForMultiShipping) {
        const found = shippingList.find((shipping) => shipping._id === shippingId);
        if (found) return found;
      }
      return undefined;
    };

    const prearrivalShippingModeSelected = getShippingModeSelected(finalContentToComputePricesOn.prearrivalShippingMode?._id);
    const primeurShippingModeSelected = getShippingModeSelected(finalContentToComputePricesOn.primeurShippingMode?._id);
    const shippingModeSelected = getShippingModeSelected(finalContentToComputePricesOn.shippingMode?._id);

    const selectedShippingModes = [
      prearrivalShippingModeSelected, primeurShippingModeSelected, shippingModeSelected
    ].filter(Boolean);

    if (
      selectedShippingModes.length !== 0 &&
        !selectedShippingModes.some((shipping) => shipping.isOffered === false)
    ) {
      return PRICE.FREE;
    }
  }

  function computeShippingCostValueWithShippingFromCart() {
    if (finalContentToComputePricesOn?.shippingMode?.isOffered || finalContentToComputePricesOn.shippingMode?.isDrive) {
      return PRICE.FREE;
    }
    return finalContentToComputePricesOn.shippingPrice;
  }

  function getTotalDiscount() {
    const value = finalContentToComputePricesOn.totalDiscountOrder;

    return {
      shouldShow: value > 0,
      label: t('account.orders.details.totalDiscountOrder'),
      value,
      description: finalContentToComputePricesOn.discountCode?.conditions
    };
  }

  function getTotalTTC() {
    return {
      shouldShow: true,
      label: t('account.orders.details.totalTtc'),
      value: finalContentToComputePricesOn.total
    };
  }

  const { t } = useTranslation();
  const storeId = useAppSelector(selectStoreId);
  const isUsa = storeId === STORE_ID.USA;
  const cartContent = useAppSelector(selectCartContent);
  const lastOrder = useAppSelector(selectLastOrder);
  const finalContentToComputePricesOn = isReportStep ? lastOrder : cartContent;

  const shippingModesForMultiShipping = Array.from(Object.entries(useAppSelector(selectShippingList)));

  const [descriptionShown, setDescriptionShown] = useState(false);
  const totalDiscountOrder = getTotalDiscount();

  const showOrHideDescription = () => {
    setDescriptionShown((prevState) => !prevState);
  };

  return {
    subTotal: getPriceLine(),
    shippingCost: getShippingCosts(),
    additionalFees: computeAdditionalFees(finalContentToComputePricesOn, t),
    shippingCostIsFree: getShippingCosts().value === PRICE.FREE,
    totalDiscountOrder: { ...totalDiscountOrder, showOrHideDescription, descriptionShown },
    totalTTC: getTotalTTC(),
    totalTva: getTotalTva(),
  };
};
